"use client";

import { useTheme } from "./ThemeProvider";

type Props = {
  /** Bildbreite in Pixel (intrinsic, CLS-Hinweis). Wird aus height + Seitenverhältnis abgeleitet, wenn nicht gesetzt. */
  width?: number;
  /** Anzeige-/Intrinsic-Höhe in Pixel. Default: 40 */
  height?: number;
  /** Tailwind-Klassen für die Anzeige-Größe. Default: "h-10 w-auto" */
  className?: string;
  /** Above-the-fold? → eager + hohe Priorität laden */
  priority?: boolean;
};

// Seitenverhältnis der Wort+Bildmarke (viewBox 672×132, Lockup 2026-09).
const LOGO_RATIO = 672 / 132;

/**
 * Einheitliches DMD-Studio-Logo mit automatischer Theme-Anpassung.
 *
 * Es gibt zwei vom Designer gelieferte Versionen (echte Markenfarben):
 *   • logo-hell.svg    – für HELLEN Hintergrund (dunkler Text)
 *   • logo-dunkel.svg  – für DUNKLEN Hintergrund (heller Text)
 * Die Schrift (Archivo, SIL Open Font License) ist in beiden Dateien als
 * Pfade eingebettet – so rendert das Logo auf jedem Gerät identisch, auch
 * ohne installierte Schrift.
 *
 * Es wird nur die passende Version gerendert: Das Theme kennt der Server
 * über das Cookie `theme` (ThemeProvider), daher kein Hydration-Mismatch
 * und kein doppelter Download beider Logos mit hoher Priorität.
 * Bewusst plain <img> (SVG, same-origin): scharf in jeder Größe.
 */
export function BrandLogo({
  width,
  height = 40,
  className = "h-10 w-auto",
  priority = false,
}: Props) {
  const { theme } = useTheme();
  const w = width ?? Math.round(height * LOGO_RATIO);
  const loadProps = priority
    ? ({ fetchPriority: "high" as const, loading: "eager" as const })
    : ({ loading: "lazy" as const });
  const src = theme === "dark" ? "/images/company/logo-dunkel.svg" : "/images/company/logo-hell.svg";

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt="DMD Studio" width={w} height={height} {...loadProps} className={className} />
  );
}
